import { addMinutes, isPast, isToday } from 'date-fns';
import { NotificationFeedbackType } from 'expo-haptics';
import type { AddressType } from '@sweetgreen/garnish';
import { triggerHapticNotificationFeedback } from '@sweetgreen/garnish';

import type {
  AvailableWantedTime,
  CardType,
  DeliveryOrderDetailInput,
  Ledger,
} from '@order/graphql';
import type { TranslationKeys, TranslationValues } from '@order/Localization';
import type { useLocalizationContext } from '@order/Localization';

import type { WeekdayTranslationKey } from '../../utils';
import { getDay, ignoreTimezone } from '../../utils';

export const isTimeAvailable = ({ disabled }: WantedTimeOption) => !disabled;

export const mapAvailableTimes = ({
  t,
  orderType,
  times,
}: WantedTimeMessage): WantedTimeOptions => {
  if (!times || !orderType) return {};

  // ───────────────────────────────────────────────────────────────

  let hasValidAsap = false;

  return times.reduce<WantedTimeOptions>((groups, { time: value }) => {
    const availableDate = ignoreTimezone(value);

    if (!availableDate) return groups;

    const { day, clock, formattedHours, formattedMinutes } =
      formatTimeParts(availableDate);

    // 10 minutes delivery window
    const deliveryWindowEnd = addMinutes(availableDate, 10);
    const {
      day: endDay,
      clock: endClock,
      formattedHours: formattedHoursEnd,
      formattedMinutes: formattedMinutesEnd,
    } = formatTimeParts(deliveryWindowEnd);

    // ────────────────────────────────────────────────────────────────────

    const hasPast = isPast(new Date(value));
    const isAsap = !hasValidAsap && !hasPast;

    if (isAsap) hasValidAsap = true;
    const isAvailableDateToday = isToday(availableDate);

    // ────────────────────────────────────────────────────────────────────

    const labelTranslationKey = generateTimeLabelKey(day, isAvailableDateToday);
    const asapLabel = isAsap ? `${t('general.asap')} - ` : '';
    const groupLabel = `${asapLabel}${t(labelTranslationKey, { day })}`;
    const groupOptions = groups[groupLabel] ?? [];

    // ────────────────────────────────────────────────────────────────────

    const pickupTimeDetails = t('general.available-time', {
      day: t(day),
      hours: formattedHours,
      minutes: formattedMinutes,
      clock,
    });

    const deliveryStartTimeDetails = t('general.available-start-time', {
      day: t(day),
      hours: formattedHours,
      minutes: formattedMinutes,
    });

    const deliveryEndTimeDetails = t('general.available-time', {
      day: t(endDay),
      hours: formattedHoursEnd,
      minutes: formattedMinutesEnd,
      clock: endClock,
    });

    const timeDetailsWithPickupLabel = `${pickupTimeDetails}`;
    const timeDetailsWithDeliveryLabel = `${deliveryStartTimeDetails}-${deliveryEndTimeDetails}`;

    // ────────────────────────────────────────────────────────────────────

    const timeOption = {
      value,
      disabled: hasPast,
      label:
        orderType === 'delivery'
          ? timeDetailsWithDeliveryLabel
          : timeDetailsWithPickupLabel,
    };

    return {
      ...groups,
      [groupLabel]: [...groupOptions, timeOption],
    };
  }, {});
};

export const getWantedTimeFormatting = (
  value: string,
  t: ReturnType<typeof useLocalizationContext>['t'],
) => {
  const availableDate = ignoreTimezone(value);

  if (!availableDate) return;

  const { clock, formattedHours, formattedMinutes } =
    formatTimeParts(availableDate);

  return t('general.available-time', {
    hours: formattedHours,
    minutes: formattedMinutes,
    clock,
  });
};

const formatTimeParts = (date: Date) => {
  const day = getDay(`${date.getDay()}`);
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const clock = hours >= 12 ? 'pm' : 'am';
  const formattedHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
  const formattedMinutes = String(minutes).padStart(2, '0');

  return {
    day,
    clock,
    formattedHours,
    formattedMinutes,
  };
};

const generateTimeLabelKey = (
  day: WeekdayTranslationKey,
  isAvailableTimeToday: boolean,
) => (isAvailableTimeToday ? 'general.available-time-today' : day);

export const applyCredit = (
  ledger: Partial<Ledger> | undefined,
  creditAmount: number,
): Partial<Ledger> | undefined => {
  const credits = ledger?.credits?.map((credit) =>
    credit.name === 'Credit' ? { ...credit, amount: creditAmount } : credit,
  );

  return (
    ledger && {
      ...ledger,
      credits,
      creditsTotal: credits?.reduce((prev, credit) => prev + credit.amount, 0),
    }
  );
};

export const applyTip = (
  ledger: Partial<Ledger> | undefined,
  tip: number,
): Partial<Ledger> | undefined => {
  return (
    ledger && {
      ...ledger,
      tip,
    }
  );
};

// The default option for delivery tips is 15%.
const DEFAULT_DELIVERY_TIP_PERCENTAGE = 15;

export const calculateTip = (
  subtotal: number | undefined,
  percentage?: number,
) => {
  return Math.round(
    ((percentage ?? DEFAULT_DELIVERY_TIP_PERCENTAGE) / 100) * (subtotal ?? 0),
  );
};

export const triggerCheckoutHapticFeedback = async (
  isCheckoutSuccess: boolean,
) => {
  const feedbackType = isCheckoutSuccess
    ? NotificationFeedbackType.Success
    : NotificationFeedbackType.Warning;

  await triggerHapticNotificationFeedback(feedbackType);
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

export type CheckoutPayload = {
  readonly wantedTime: string;
  readonly billingAccountId: string;
  readonly useCredit: boolean;
  readonly dropoffLocationId: string | null;
  readonly contactNumber: string | null;
  readonly includeUtensils: boolean;
  readonly deliveryOrderDetail?: DeliveryOrderDetailInput | null;
};

type WantedTimeOptions = Record<string, readonly WantedTimeOption[]>;

type WantedTimeOption = {
  readonly value: string;
  readonly label: string;
  readonly disabled: boolean;
};

type WantedTimeMessage = {
  readonly t: (key: TranslationKeys, values?: TranslationValues) => string;
  readonly orderType?: AddressType;
  readonly times?: readonly AvailableWantedTime[];
};

export type CreditCardOption = {
  readonly value: string;
  readonly label: string;
  readonly cardType: CardType | 'NEW';
};

export type SelectedTimeProps = {
  readonly selectedTime: string;
  readonly setSelectedTime: (time: string) => void;
};
